'use client';

import React from 'react';
import { Search, MapPin, Calendar, Car, Star, ArrowRight } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { useStorefrontData } from '@/lib/storefront/storefront-data';
import { containerClass, sectionStyle } from './HeaderSections';

const DEFAULT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1600&q=80';

// ──────────────────────────────────────────────────────────────
// 1. Centered hero with search bar
// ──────────────────────────────────────────────────────────────

export function HeroCentered({ content, style }: SectionProps) {
  const { preview } = useStorefrontData();
  const {
    eyebrow = 'Premium car rental in Agadir',
    headline = 'Find your perfect ride',
    subheadline = 'Book a car in minutes. Free cancellation, no hidden fees, delivery to your hotel.',
    ctaText = 'Browse vehicles',
    ctaHref = '/vehicles',
    secondaryCtaText = 'How it works',
    secondaryCtaHref = '/services',
    showSearch = true,
    imageUrl = DEFAULT_HERO_IMAGE,
    overlay = 70,
  } = content;

  return (
    <section className="relative w-full overflow-hidden" style={sectionStyle(style)}>
      {imageUrl && (
        <div className="absolute inset-0">
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950" style={{ opacity: overlay / 100 }} />
        </div>
      )}
      <div className={`relative ${containerClass(style)}`}>
        <div className="mx-auto max-w-3xl py-10 text-center sm:py-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            <Star className="h-3.5 w-3.5 text-amber-400" />
            {eyebrow}
          </span>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">{headline}</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/80 sm:text-lg">{subheadline}</p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a href={safeHref(ctaHref)} className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </a>
            {secondaryCtaText && (
              <a href={safeHref(secondaryCtaHref)} className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
                {secondaryCtaText}
              </a>
            )}
          </div>

          {showSearch && (
            <form
              action="/vehicles"
              method="get"
              onSubmit={(event) => {
                // Inside the builder the form is a preview, not a real search.
                if (preview) event.preventDefault();
              }}
              className="mx-auto mt-10 max-w-3xl rounded-2xl bg-white p-3 shadow-2xl"
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-left">
                  <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                  <input
                    name="pickup"
                    aria-label="Pick-up city"
                    className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
                    placeholder="Pick-up city"
                  />
                </label>
                <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-left">
                  <Calendar className="h-4 w-4 shrink-0 text-slate-400" />
                  <input name="from" aria-label="Pick-up date" type="date" className="w-full bg-transparent text-sm text-slate-900 outline-none" />
                </label>
                <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-left">
                  <Calendar className="h-4 w-4 shrink-0 text-slate-400" />
                  <input name="to" aria-label="Return date" type="date" className="w-full bg-transparent text-sm text-slate-900 outline-none" />
                </label>
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 sm:col-span-2 lg:col-span-1"
                >
                  <Search className="h-4 w-4" />
                  Search
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 2. Split hero — text left, image right
// ──────────────────────────────────────────────────────────────

export function HeroSplit({ content, style }: SectionProps) {
  const {
    eyebrow = 'Trusted by 12,000+ travellers',
    headline = 'Drive the coast in style',
    subheadline = 'A modern fleet, transparent prices and roadside assistance included with every booking.',
    ctaText = 'See the fleet',
    ctaHref = '/vehicles',
    secondaryCtaText = 'Talk to us',
    secondaryCtaHref = '/contact',
    imageUrl = DEFAULT_HERO_IMAGE,
    bullets = ['Free cancellation up to 24h', 'Unlimited mileage options', '24/7 roadside assistance'],
  } = content;

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} grid items-center gap-10 lg:grid-cols-2`}>
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-brand">{eyebrow}</span>
          <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{headline}</h1>
          <p className="mt-4 text-base text-slate-600 sm:text-lg">{subheadline}</p>

          <ul className="mt-6 space-y-2">
            {bullets.map((b: string, i: number) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">✓</span>
                {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href={safeHref(ctaHref)} className="inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
              {ctaText}
            </a>
            {secondaryCtaText && (
              <a href={safeHref(secondaryCtaHref)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                {secondaryCtaText}
              </a>
            )}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 -z-0 rounded-[2rem] bg-gradient-to-tr from-brand/20 to-transparent" />
          <img src={imageUrl} alt={headline} className="relative z-10 aspect-[4/3] w-full rounded-[1.75rem] object-cover shadow-xl" />
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 3. Full-bleed overlay hero — copy bottom-left
// ──────────────────────────────────────────────────────────────

export function HeroFullBleed({ content, style }: SectionProps) {
  const {
    headline = 'Your journey starts here',
    subheadline = 'Hand-picked vehicles for every road trip along Morocco’s Atlantic coast.',
    ctaText = 'Book now',
    ctaHref = '/vehicles',
    imageUrl = DEFAULT_HERO_IMAGE,
    overlay = 55,
    stats = [
      { value: '120+', label: 'Vehicles' },
      { value: '4.9/5', label: 'Rated by guests' },
      { value: '24/7', label: 'Support' },
    ],
  } = content;

  return (
    <section className="relative flex min-h-[560px] w-full items-end overflow-hidden" style={sectionStyle(style)}>
      {imageUrl && (
        <div className="absolute inset-0">
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-slate-950" style={{ opacity: overlay / 100 }} />
        </div>
      )}

      <div className={`relative ${containerClass(style)} py-12`}>
        <div className="max-w-2xl">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">{headline}</h1>
          <p className="mt-4 text-base text-white/85 sm:text-lg">{subheadline}</p>
          <a href={safeHref(ctaHref)} className="mt-7 inline-flex items-center gap-2 rounded-xl bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand/90">
            {ctaText}
          </a>
        </div>

        {stats.length > 0 && (
          <div className="mt-10 grid max-w-2xl grid-cols-3 gap-4 border-t border-white/20 pt-6">
            {stats.map((s: any, i: number) => (
              <div key={i}>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs uppercase tracking-wide text-white/70">{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 4. Minimal hero — no image, big type, soft background
// ──────────────────────────────────────────────────────────────

export function HeroMinimal({ content, style }: SectionProps) {
  const {
    eyebrow = 'Car rental, simplified',
    headline = 'Rent a car in three taps',
    subheadline = 'Pick a vehicle, choose your dates, drive away. No paperwork, no queues.',
    ctaText = 'Start booking',
    ctaHref = '/vehicles',
    secondaryCtaText = 'View prices',
    secondaryCtaHref = '/deals',
  } = content;

  return (
    <section className="w-full bg-gradient-to-b from-brand/10 via-white to-white" style={sectionStyle(style)}>
      <div className={`${containerClass(style)} py-14 text-center sm:py-20`}>
        <span className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-white px-3 py-1 text-xs font-semibold text-brand">
          <Car className="h-3.5 w-3.5" />
          {eyebrow}
        </span>
        <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl">{headline}</h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{subheadline}</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href={safeHref(ctaHref)} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-700">
            {ctaText}
            <ArrowRight className="h-4 w-4" />
          </a>
          {secondaryCtaText && (
            <a href={safeHref(secondaryCtaHref)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">
              {secondaryCtaText}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// 5. Video-style hero (image treated as poster with play affordance)
// ──────────────────────────────────────────────────────────────

export function HeroMedia({ content, style }: SectionProps) {
  const {
    headline = 'See the fleet in motion',
    subheadline = 'A short look at the cars and the service you can expect.',
    videoUrl = '',
    imageUrl = DEFAULT_HERO_IMAGE,
    ctaText = 'Book a test drive',
    ctaHref = '/vehicles',
  } = content;

  return (
    <section className="relative w-full overflow-hidden bg-slate-950" style={sectionStyle(style)}>
      {videoUrl ? (
        <video className="absolute inset-0 h-full w-full object-cover opacity-60" src={videoUrl} poster={imageUrl} autoPlay muted loop playsInline />
      ) : (
        <div className="absolute inset-0">
          <img src={imageUrl} alt="" className="h-full w-full object-cover opacity-60" />
        </div>
      )}
      <div className={`relative ${containerClass(style)} py-16 text-center sm:py-24`}>
        <h1 className="mx-auto max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">{headline}</h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-white/80">{subheadline}</p>

        <div className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur">
          ▶
        </div>

        <a href={safeHref(ctaHref)} className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-white/90">
          {ctaText}
        </a>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Registry
// ──────────────────────────────────────────────────────────────

export const HERO_VARIATIONS = {
  centered: HeroCentered,
  split: HeroSplit,
  full_bleed: HeroFullBleed,
  minimal: HeroMinimal,
  media: HeroMedia,
} as const;

export type HeroVariationKey = keyof typeof HERO_VARIATIONS;
